import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import Button from '@components/_shared/button';
import { ListingEstimateDateRangePicker } from 'components/listing/listingPricing/listingEstimateDateRangePicker';
import { DiscountTextContainer } from '@components/listing/listingPricing/discountTextContainer';
import Checkbox from '@components/_shared/checkbox';
import { useGetListingPrice } from '@utils/hooks/api/useGetListingPrice';
import { toDollars } from '@utils/money';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { DatesRange } from '@utils/types/calendar';
import {
  EstimateUpsellAlternativeType,
  ListingPriceEstimateType,
} from '@utils/types/estimate';
import { defineSplitPayment } from '@components/payment/utils';
import { parseDateWithoutTimezone } from '@utils/dates';
import { AirbnbComparison } from '@components/listing/listingPricing/airbnbComparison';
import {
  calculateAverageTotalPerMonth,
  calculateAverageTotalPerNight,
} from '@utils/pricing';
import { PriceLine } from '@components/_shared/priceLine';
import MessageHostModal from '@components/listing/messageHostModal';
import { prepareArrayOfDisabledDates } from '@components/_shared/datePickers/helpers';

import styles from './styles.module.scss';

type Props = {
  listingId: string;
  listingTitle: string;
  reservedDates: { start: string; end: string }[];
  nightlyPrices: Record<string, Record<string, number[]>>;
  calendarMinNights: Record<string, Record<string, number[]>>;
  onBookNowClick: (
    data: ListingPriceEstimateType,
    dateRange: DatesRange,
    petFee?: boolean,
    payMonthly?: boolean,
    upsellAlternative?: EstimateUpsellAlternativeType
  ) => void;
  averageMonthlyPrice: number;
  listingHasPetFee?: boolean;
  minStay?: number;
  maxStay?: number;
  weeklyPriceFactor?: number;
  monthlyPriceFactor?: number;
  firstAvailableDate?: string | null;
  lastKnownDay?: string | null;
  sixtyDaysAveragePrice?: number;
  isConversationEnabled?: boolean;
  highestPrice?: number;
} & BaseComponentProps;

ListingPricing.defaultProps = {
  listingHasPetFee: false,
  firstAvailableDate: null,
  lastKnownDay: null,
  reservedDates: [],
  nightlyPrices: {},
  calendarMinNights: {},
};

export function ListingPricing(props: Props) {
  const {
    containerStyle,
    listingId,
    listingTitle,
    minStay,
    maxStay,
    weeklyPriceFactor,
    nightlyPrices,
    monthlyPriceFactor,
    firstAvailableDate,
    lastKnownDay,
    onBookNowClick,
    reservedDates,
    calendarMinNights,
    listingHasPetFee,
    averageMonthlyPrice,
    sixtyDaysAveragePrice,
    isConversationEnabled,
    highestPrice = 0,
  } = props;
  const router = useRouter();
  const [dateRange, setDateRange] = useState<DatesRange>({
    from: null,
    to: null,
  });
  const [withPetFee, setWithPetFee] = useState(false);
  const [upsellAlternative, setUpsellAlternative] = useState<
    EstimateUpsellAlternativeType | undefined
  >();
  const [isMessageHostModalOpen, setIsMessageHostModalOpen] = useState(false);

  const isInstantBooking = minStay && minStay < 30;

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('messages')) {
      setIsMessageHostModalOpen(true);
    }
  }, []);

  const onMessageHostClick = () => {
    setIsMessageHostModalOpen(true);
    router.query.messages = 'open';
    router.replace(
      { pathname: router.pathname, query: router.query },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  const onCloseMessageHostModal = () => {
    setIsMessageHostModalOpen(false);
    delete router.query.messages;
    router.replace(
      { pathname: router.pathname, query: router.query },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  useEffect(() => {
    if (router.query.petFriendly === 'true') {
      setWithPetFee(true);
    }
  }, [router.query.petFriendly]);

  const changeDateRange = (dates: DatesRange) => {
    setDateRange(dates);

    const savedDates = {
      from: dates.from ? dates.from : null,
      to: dates.to ? dates.to : null,
    };

    /**
     * We do not want to put upsell dates to query params
     */
    if (upsellAlternative?.originalStart) {
      savedDates.from = parseDateWithoutTimezone(
        upsellAlternative.originalStart
      );
    }
    if (upsellAlternative?.originalEnd) {
      savedDates.to = parseDateWithoutTimezone(upsellAlternative.originalEnd);
    }

    if (savedDates.from && savedDates.to) {
      try {
        router.query.dates = [
          savedDates.from.toISOString(),
          savedDates.to.toISOString(),
        ];
      } catch (e) {
        // @ts-ignore
        router.query.dates = [savedDates.from, savedDates.to];
      }
      router.push(router, undefined, { scroll: false, shallow: false });
    }
  };

  const canSplitPayment = useMemo(
    () => defineSplitPayment(dateRange),
    [dateRange.to, dateRange.from]
  );

  const { data, isFetching } = useGetListingPrice({
    listingId,
    dateStart: upsellAlternative?.id
      ? parseDateWithoutTimezone(upsellAlternative?.originalStart!)
      : dateRange.from,
    dateEnd: upsellAlternative?.id
      ? parseDateWithoutTimezone(upsellAlternative?.originalEnd!)
      : dateRange.to,
    withPetFee,
    upsellAlternative: upsellAlternative?.id,
  });

  const { nightlyPrice, monthlyPrice } = useMemo(() => {
    return {
      nightlyPrice: data
        ? calculateAverageTotalPerNight(
            data.amount - data.taxes,
            data.number_of_nights
          )
        : 0,
      monthlyPrice: data
        ? calculateAverageTotalPerMonth(
            data.amount - data.taxes,
            data.number_of_nights
          )
        : averageMonthlyPrice,
    };
  }, [data?.amount, data?.taxes, data?.number_of_nights, averageMonthlyPrice]);

  const onPetFeeClick = (value: boolean) => {
    setWithPetFee(value);
    router.query.petFriendly = value.toString();
    router.push(router, undefined, { scroll: false, shallow: false });
  };

  const disabledDates = useMemo(() => {
    return prepareArrayOfDisabledDates(reservedDates);
  }, [reservedDates]);

  return (
    <div
      className={clsx([styles.listingPricePerDatesContainer, containerStyle])}
    >
      <div className={styles.header}>
        {data || isFetching ? (
          <>
            {data ? (
              <>
                {isInstantBooking ? (
                  <div className={styles.price}>
                    {toDollars(nightlyPrice, 0)}
                    <div className={styles.priceLabel}>per night</div>
                  </div>
                ) : (
                  <div className={styles.price}>
                    {monthlyPrice < highestPrice ? (
                      <span>{toDollars(highestPrice, 0)}</span>
                    ) : null}
                    {toDollars(monthlyPrice, 0)}
                  </div>
                )}

                {!isInstantBooking && (
                  <div className={styles.priceSubheading}>
                    avg. price per month before taxes
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  className="skeleton-box"
                  style={{
                    marginBottom: '8px',
                    width: 250,
                    height: 40,
                    borderRadius: '12px',
                  }}
                />
                <div
                  className="skeleton-box"
                  style={{ width: 170, height: 26, borderRadius: '8px' }}
                />
              </>
            )}
          </>
        ) : (
          <div className={styles.placeholder}>
            <span>
              Select <b>check-in</b> and <b>check-out</b> dates to see the price
              of this property.
            </span>
          </div>
        )}
      </div>

      <div className={styles.divider} />

      {isInstantBooking && (
        <DiscountTextContainer
          isHidden={!!data}
          weeklyPriceFactor={weeklyPriceFactor}
          monthlyPriceFactor={monthlyPriceFactor}
          containerStyle={styles.discountContainer}
        />
      )}

      <div className={styles.datesSelector}>
        <ListingEstimateDateRangePicker
          listingId={listingId}
          nightlyPrices={nightlyPrices}
          reservedDates={reservedDates}
          calendarMinNights={calendarMinNights}
          maxAmount={maxStay}
          minAmount={minStay}
          onRangeSelected={changeDateRange}
          firstAvailableDate={firstAvailableDate}
          lastKnownDay={lastKnownDay}
          upsellAlternative={upsellAlternative}
          setUpsellAlternative={setUpsellAlternative}
          withPetFee={withPetFee}
        />
      </div>

      {listingHasPetFee && (
        <Checkbox
          label="Have Pets?"
          value={withPetFee}
          onChange={onPetFeeClick}
          variant="newGreen"
          containerStyle={styles.checkboxContainer}
        />
      )}

      {!isInstantBooking && (
        <div className={styles.youWontPayAnything}>
          You won’t pay anything until Rove accepts your request.
        </div>
      )}

      {isInstantBooking && dateRange.from && dateRange.to && data?.amount && (
        <PriceLine
          label="Total before taxes"
          amount={data.amount - data.taxes}
          containerStyle={styles.priceLine}
        />
      )}

      <Button
        disabled={!data}
        onClick={() =>
          onBookNowClick(
            data!,
            dateRange,
            withPetFee,
            canSplitPayment,
            upsellAlternative
          )
        }
        variant="blueContained"
        containerStyle={styles.bookButton}
      >
        {isInstantBooking ? 'Book now' : 'Reserve'}
      </Button>

      {isConversationEnabled ? (
        <Button
          onClick={onMessageHostClick}
          variant="darkTanContained"
          containerStyle={styles.chatButton}
        >
          Message Host
        </Button>
      ) : (
        <Button
          // @ts-ignore
          onClick={() => window?.FrontChat('show')}
          variant="darkContained"
          containerStyle={styles.chatButton}
        >
          <div className={styles.chatButton__text}>
            <i />
            Chat with Concierge Team
          </div>
        </Button>
      )}

      {data?.amount && (
        <AirbnbComparison
          amount={isInstantBooking ? data.amount - data.taxes : monthlyPrice}
        />
      )}

      <MessageHostModal
        isOpen={isMessageHostModalOpen}
        onClose={onCloseMessageHostModal}
        listingTitle={listingTitle}
        listingId={listingId}
        disabledDates={disabledDates}
        nightlyPrices={nightlyPrices}
        minNightsStay={minStay}
        maxNightsStay={maxStay}
        firstAvailableDate={firstAvailableDate}
        lastKnownDay={lastKnownDay}
      />
    </div>
  );
}
